import { createElement, type DOMAttributes, type ReactNode } from "react";
import {
  FP_LINES_FILTERS,
  FP_PAGE_NUMBER_FORMAT_ATTRIBUTE,
  FP_PAGE_NUMBER_FORMATS,
  FP_SHOW_WHEN_ATTRIBUTE,
  type FpLinesFilter,
  type FpPageNumberFormat,
} from "@forgepoint/shared";
import { FitToPageContent } from "@/modules/publicQuote/components/FitToPageContent";
import {
  PAGE_PADDING_X,
  PAGE_PADDING_Y,
} from "@/modules/publicQuote/components/PdfPage";
import { EmptyChromePlaceholder } from "./EmptyChromePlaceholder";
import { EmptyTextPlaceholder } from "./EmptyTextPlaceholder";
import { FpBreaks } from "./FpBreaks";
import { buildFpColumns, FpColumns } from "./FpColumns";
import { FpDivider } from "./FpDivider";
import { FpField } from "./FpField";
import { FpImage } from "./FpImage";
import { FpLineField } from "./FpLineField";
import { FpLines } from "./FpLines";
import { FpMarkdown } from "./FpMarkdown";
import { FpPageCount } from "./FpPageCount";
import { FpPageNumber } from "./FpPageNumber";
import { FpShowWhen } from "./FpShowWhen";
import { FpTable } from "./FpTable";
import { FpText } from "./FpText";
import {
  containerStyle,
  containerStyleWithoutSize,
  readContainerValues,
  resolveDimensionValue,
} from "./fpContainer";
import { parseNumberSourceAttribute } from "./lineFields";
import { isEmptyProseBlock } from "./textPlaceholder";

/** Prop bag for dynamically converted DOM elements. */
type ConvertedProps = DOMAttributes<Element> & Record<string, unknown>;

/** HTML attribute names that React expects under a different prop name. */
const ATTRIBUTE_NAME_MAP: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  colspan: "colSpan",
  rowspan: "rowSpan",
  cellspacing: "cellSpacing",
  cellpadding: "cellPadding",
  tabindex: "tabIndex",
  readonly: "readOnly",
  maxlength: "maxLength",
  srcset: "srcSet",
  autocomplete: "autoComplete",
  spellcheck: "spellCheck",
};

/** Elements that must not receive children when created via React. */
const VOID_ELEMENT_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const DEFAULT_FP_IMAGE_HEIGHT = "120px";

/**
 * Converts a parsed DOM element into a React element tree.
 * @param element - The DOM element to convert
 * @param key - The React key for the produced node
 * @returns The converted React node
 */
export function domElementToReact(element: Element, key: string): ReactNode {
  const converted = convertElement(element, key);
  if (element.hasAttribute("data-fp-fit-page")) {
    return <FitToPageContent key={`fit-${key}`}>{converted}</FitToPageContent>;
  }
  return converted;
}

/**
 * Converts all child nodes of a DOM node into React nodes.
 * @param parent - The DOM node whose children are converted
 * @param keyPrefix - The React key prefix for the produced child nodes
 * @returns The converted child nodes
 */
function domChildNodesToReact(parent: Node, keyPrefix: string): ReactNode[] {
  return Array.from(parent.childNodes)
    .map((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return child.nodeValue;
      }
      if (child instanceof Element) {
        return domElementToReact(child, `${keyPrefix}-${index}`);
      }
      return null;
    })
    .filter((node) => node !== null);
}

/**
 * Converts an element, gating it behind its `data-fp-show-when` condition when
 * it carries one.
 * @param element - The DOM element to convert
 * @param key - The React key for the produced node
 * @returns The converted React node, gated by its condition
 */
function convertElement(element: Element, key: string): ReactNode {
  const condition = element.getAttribute(FP_SHOW_WHEN_ATTRIBUTE);
  const converted = convertUngatedElement(element, key);
  if (condition === null) {
    return converted;
  }
  return (
    <FpShowWhen key={`when-${key}`} condition={condition}>
      {converted}
    </FpShowWhen>
  );
}

/**
 * Dispatches an element to the fp-* component map or the generic converter.
 * An empty prose block gets the editor-only ghost hint as its sole child, so
 * it stays visible and clickable on the canvas while staying empty in source.
 * @param element - The DOM element to convert
 * @param key - The React key for the produced node
 * @returns The converted React node
 */
function convertUngatedElement(element: Element, key: string): ReactNode {
  const tagName = element.tagName.toLowerCase();
  if (tagName.startsWith("fp-")) {
    return convertFpElement(element, tagName, key);
  }

  const props = buildElementProps(element, key);
  if (VOID_ELEMENT_TAGS.has(tagName)) {
    return createElement(tagName, props);
  }
  if (isEmptyProseBlock(element)) {
    return createElement(
      tagName,
      props,
      <EmptyTextPlaceholder key={`empty-${key}`} />,
    );
  }
  if (element.childNodes.length === 0) {
    return createElement(tagName, props);
  }
  return createElement(tagName, props, ...domChildNodesToReact(element, key));
}

/**
 * Maps an fp-* custom tag to its dedicated React component, or null. The grid
 * tags normally render through their parent, which reads them positionally -
 * `<fp-cell>` as a column through `buildFpColumns`, `<fp-column>`, `<fp-row>`
 * and `<fp-cell>` through `<fp-table>` - so one reaching this switch sits
 * outside its parent: it draws its own box and its children rather than
 * dropping the authored subtree.
 * @param element - The fp-* DOM element to convert
 * @param tagName - The lowercased tag name of the element
 * @param key - The React key for the produced node
 * @returns The dedicated fp-* component, or null for an unknown tag
 */
function convertFpElement(
  element: Element,
  tagName: string,
  key: string,
): ReactNode {
  const common = {
    className: element.getAttribute("class") ?? undefined,
    style: parseStyleAttribute(element.getAttribute("style")),
    "data-fp-node": element.getAttribute("data-fp-node") ?? undefined,
  };
  const boxStyle = {
    ...containerStyle(readContainerValues(element)),
    ...common.style,
  };

  switch (tagName) {
    case "fp-text":
      return (
        <FpText
          key={key}
          style={boxStyle}
          data-fp-node={common["data-fp-node"]}
          source={element.getAttribute("source")}
          content={element.textContent ?? ""}
        />
      );
    case "fp-columns":
      return (
        <FpColumns
          key={key}
          style={boxStyle}
          data-fp-node={common["data-fp-node"]}
          columns={buildFpColumns(
            element,
            key,
            domElementToReact,
            domChildNodesToReact,
          )}
        />
      );
    case "fp-table":
      return (
        <FpTable
          key={key}
          className={common.className}
          style={boxStyle}
          data-fp-node={common["data-fp-node"]}
          element={element}
          renderChildren={domChildNodesToReact}
        />
      );
    case "fp-divider":
      return (
        <FpDivider
          key={key}
          style={boxStyle}
          data-fp-node={common["data-fp-node"]}
        />
      );
    case "fp-header":
    case "fp-footer": {
      // The band bleeds into the page margins: negative margins reach the
      // physical page edges while the matching padding keeps the authored
      // content inside them, so a band background paints edge to edge and the
      // net flow size stays what the paginator measured.
      const isHeader = tagName === "fp-header";
      const { background, backgroundColor, ...contentStyle } = boxStyle;
      return (
        <div
          key={key}
          style={{
            background,
            backgroundColor,
            margin: isHeader
              ? `${-PAGE_PADDING_Y}px ${-PAGE_PADDING_X}px 0`
              : `0 ${-PAGE_PADDING_X}px ${-PAGE_PADDING_Y}px`,
            padding: isHeader
              ? `${PAGE_PADDING_Y}px ${PAGE_PADDING_X}px 0`
              : `0 ${PAGE_PADDING_X}px ${PAGE_PADDING_Y}px`,
          }}
          data-fp-node={common["data-fp-node"]}
        >
          <div style={contentStyle}>
            {element.children.length === 0 &&
            (element.textContent ?? "").trim() === "" ? (
              <EmptyChromePlaceholder
                key={`empty-${key}`}
                kind={isHeader ? "header" : "footer"}
              />
            ) : (
              domChildNodesToReact(element, key)
            )}
          </div>
        </div>
      );
    }
    case "fp-column":
    case "fp-row":
    case "fp-cell":
    case "fp-section-heading":
    case "fp-section-notes":
      return (
        <div key={key} style={boxStyle} data-fp-node={common["data-fp-node"]}>
          {domChildNodesToReact(element, key)}
        </div>
      );
    case "fp-field":
      return (
        <FpField
          key={key}
          {...common}
          style={boxStyle}
          source={element.getAttribute("source")}
        />
      );
    case "fp-markdown":
      return (
        <FpMarkdown
          key={key}
          {...common}
          source={element.getAttribute("source")}
          content={element.textContent ?? ""}
        />
      );
    case "fp-lines":
      return (
        <FpLines
          key={key}
          filter={parseLinesFilterAttribute(element.getAttribute("filter"))}
          numberFieldName={parseNumberSourceAttribute(
            element.getAttribute("number-source"),
          )}
          stampTemplate={buildTemplateStamp(element, key)}
        />
      );
    case "fp-breaks":
      return (
        <FpBreaks key={key} stampTemplate={buildTemplateStamp(element, key)} />
      );
    case "fp-line-field":
      return (
        <FpLineField
          key={key}
          {...common}
          style={boxStyle}
          source={element.getAttribute("source")}
        />
      );

    case "fp-image": {
      const height = resolveDimensionValue(element.getAttribute("height"));
      const width = resolveDimensionValue(element.getAttribute("width"));
      return (
        <FpImage
          key={key}
          {...common}
          style={{
            ...containerStyleWithoutSize(readContainerValues(element)),
            ...common.style,
          }}
          assetId={element.getAttribute("asset-id")}
          height={height ?? (width === null ? DEFAULT_FP_IMAGE_HEIGHT : null)}
          width={width}
        />
      );
    }
    case "fp-page-number":
      return (
        <FpPageNumber
          key={key}
          {...common}
          format={parsePageNumberFormatAttribute(
            element.getAttribute(FP_PAGE_NUMBER_FORMAT_ATTRIBUTE),
          )}
          margin={element.getAttribute("margin")}
        />
      );
    case "fp-page-count":
      return (
        <FpPageCount
          key={key}
          {...common}
          margin={element.getAttribute("margin")}
        />
      );
    default:
      return null;
  }
}

/**
 * Parses an fp-lines `filter` attribute, defaulting to `all` when absent or
 * unrecognized.
 * @param value - The raw `filter` attribute value, or null when absent
 * @returns The parsed line filter
 */
function parseLinesFilterAttribute(value: string | null): FpLinesFilter {
  const filter = FP_LINES_FILTERS.find((candidate) => candidate === value);
  return filter ?? "all";
}

/**
 * Parses an `fp-page-number` `format` attribute, defaulting to the bare page
 * number when absent or unrecognized.
 * @param value - The raw `format` attribute value, or null when absent
 * @returns The parsed counter format
 */
function parsePageNumberFormatAttribute(
  value: string | null,
): FpPageNumberFormat {
  const format = FP_PAGE_NUMBER_FORMATS.find(
    (candidate) => candidate === value,
  );
  return format ?? "n";
}

/**
 * Builds a repeater's template stamp: a function converting the element's
 * single `<template>` child's content per stamp, or null without a template.
 * When the repeater is a top-level block (carries a `data-fp-node` id), each
 * stamped instance root is marked with `data-fp-instance-of` so the editor can
 * trace it back to the owning repeater and treat the whole repeater as opaque.
 * @param element - The repeater element whose template child is stamped
 * @param key - The React key prefix of the repeater element
 * @returns The stamp function, or null when no template child exists
 */
function buildTemplateStamp(
  element: Element,
  key: string,
): ((stampKey: string) => ReactNode[]) | null {
  const template = Array.from(element.children).find(
    (child): child is HTMLTemplateElement =>
      child instanceof HTMLTemplateElement,
  );
  if (!template) {
    return null;
  }
  const instanceOwnerId = element.getAttribute("data-fp-node");
  if (instanceOwnerId !== null) {
    markInstanceRoots(template.content, instanceOwnerId);
  }
  return (stampKey: string) =>
    domChildNodesToReact(template.content, `${key}-${stampKey}`);
}

/**
 * Stamps `data-fp-instance-of` on the top-level element children of a
 * repeater's template content, tracing each stamped instance to its owner.
 * @param content - The repeater's `<template>` content fragment
 * @param ownerNodeId - The owning repeater's positional node id
 */
function markInstanceRoots(
  content: DocumentFragment,
  ownerNodeId: string,
): void {
  Array.from(content.children).forEach((child) =>
    child.setAttribute("data-fp-instance-of", ownerNodeId),
  );
}

/**
 * Builds the React props for a plain converted element from its DOM attributes.
 * @param element - The DOM element whose attributes become props
 * @param key - The React key for the produced node
 * @returns The React prop bag, with attribute names mapped and styles parsed
 */
function buildElementProps(element: Element, key: string): ConvertedProps {
  const props: ConvertedProps = { key };
  Array.from(element.attributes).forEach((attribute) => {
    if (attribute.name === "style") {
      props.style = parseStyleAttribute(attribute.value);
      return;
    }
    const propName = ATTRIBUTE_NAME_MAP[attribute.name] ?? attribute.name;
    props[propName] = attribute.value;
  });
  return props;
}

/**
 * Parses an inline style attribute string into a React style object.
 * @param styleText - The raw inline style attribute, or null when absent
 * @returns The React style object, or undefined when empty or absent
 */
function parseStyleAttribute(
  styleText: string | null,
): Record<string, string> | undefined {
  if (!styleText) {
    return undefined;
  }
  const style: Record<string, string> = {};
  styleText.split(";").forEach((declaration) => {
    const colonIndex = declaration.indexOf(":");
    if (colonIndex === -1) {
      return;
    }
    const property = declaration.slice(0, colonIndex).trim();
    const value = declaration.slice(colonIndex + 1).trim();
    if (!property || !value) {
      return;
    }
    style[camelCaseStyleProperty(property)] = value;
  });
  return Object.keys(style).length > 0 ? style : undefined;
}

/**
 * Converts a kebab-case CSS property name to the camelCase form React expects.
 * @param property - The CSS property name
 * @returns The camelCased property name, or the original for custom properties
 */
function camelCaseStyleProperty(property: string): string {
  if (property.startsWith("--")) {
    return property;
  }
  return property.replace(/-([a-z])/g, (_match: string, letter: string) =>
    letter.toUpperCase(),
  );
}
